import { collection, addDoc, getDocs, doc, updateDoc, query, where, getDoc, onSnapshot, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getAuth } from 'firebase/auth';
import type { TradingAccount } from '../types';
import { getApiUrl } from '../config/api';
import { applyBasicTierAccountState, clearBasicTierAccountLockCache, syncBasicTierAccountLock } from '../lib/basicTierAccountLock';
import {
  getAuthBasicTierLimitedHint,
  resolveIsBasicLimitedTier,
  subscribeBasicTierLockRevision,
} from '../lib/basicTierLimits';

const ACCOUNTS_COLLECTION = 'accounts';
const USERS_COLLECTION = 'users';
const TRIAL_ACCOUNT_LOCKS_COLLECTION = 'trialAccountLocks';

const auth = getAuth();

type AccountsSubscriber = (accounts: TradingAccount[]) => void;

let sharedUserId: string | null = null;
let sharedUnsubQuery: (() => void) | null = null;
let sharedUnsubRevision: (() => void) | null = null;
let sharedLastAccounts: TradingAccount[] = [];
let sharedAccountsFingerprint: string | null = null;
const sharedSubscribers = new Set<AccountsSubscriber>();

function accountsFingerprint(accounts: TradingAccount[]): string {
  return accounts
    .map((account) => [
      account.id,
      account.accountVerified ?? '',
      account.deleted ? '1' : '0',
      account.isDisabled ? '1' : '0',
      account.name ?? '',
      account.lastError ?? '',
    ].join(':'))
    .sort()
    .join('|');
}

function teardownSharedAccountsSubscription() {
  sharedUnsubQuery?.();
  sharedUnsubRevision?.();
  sharedUnsubQuery = null;
  sharedUnsubRevision = null;
  sharedUserId = null;
  sharedLastAccounts = [];
  sharedAccountsFingerprint = null;
}

function notifySharedSubscribers(accounts: TradingAccount[]) {
  sharedSubscribers.forEach((callback) => {
    try {
      callback(accounts);
    } catch (error) {
      console.error('[accountService] Shared accounts subscriber failed:', error);
    }
  });
}

async function emitSharedAccounts(accounts: TradingAccount[], userId: string) {
  sharedLastAccounts = accounts;

  try {
    const isLimitedTier = await resolveAccountLockTier(userId);
    notifySharedSubscribers(applyBasicTierAccountState(accounts, isLimitedTier));
    await syncBasicTierAccountLock(accounts, isLimitedTier);
  } catch (error) {
    console.error('[accountService] Basic tier account sync failed:', error);
    try {
      const isLimitedTier = await resolveIsBasicLimitedTier(userId);
      notifySharedSubscribers(applyBasicTierAccountState(accounts, isLimitedTier));
    } catch {
      notifySharedSubscribers(accounts);
    }
  }
}

// Écoute en temps réel de TOUS les comptes de l'utilisateur (une seule requête Firestore).
// Coût : 1 lecture par compte à l'ouverture, puis 1 lecture uniquement pour chaque
// document ajouté/modifié/supprimé ensuite — pas de polling, pas de boucle de lecture.
function ensureSharedAccountsSubscription(userId: string) {
  if (sharedUserId === userId && sharedUnsubQuery) {
    return;
  }

  teardownSharedAccountsSubscription();
  sharedUserId = userId;

  const accountsRef = collection(db, ACCOUNTS_COLLECTION);
  const q = query(accountsRef, where('userId', '==', userId));

  sharedUnsubQuery = onSnapshot(q, (snapshot) => {
    const accounts = snapshot.docs
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      } as TradingAccount))
      .filter((account) => !isPendingPermanentDeletion(account));

    const fingerprint = accountsFingerprint(accounts);
    if (fingerprint === sharedAccountsFingerprint) {
      return;
    }
    sharedAccountsFingerprint = fingerprint;
    void emitSharedAccounts(accounts, userId);
  }, (error) => {
    console.error('[accountService] Accounts query listener error:', error);
  });

  sharedUnsubRevision = subscribeBasicTierLockRevision(() => {
    if (sharedLastAccounts.length === 0) return;
    clearBasicTierAccountLockCache();
    void emitSharedAccounts(sharedLastAccounts, userId);
  });
}

// Compte en corbeille dont la suppression définitive est demandée : la purge serveur est en
// cours ou planifiée, il ne doit plus apparaître nulle part (même plus dans la corbeille).
function isPendingPermanentDeletion(account: TradingAccount): boolean {
  return account.deleted === true && Boolean(account.permanentDeletionRequestedAt);
}

// Mise en corbeille et suppression définitive sont exécutées par le proxy (Admin SDK) :
// le client n'écrit rien lui-même et ne touche jamais aux trades.
async function callAccountDeletionEndpoint(accountId: string, action: 'delete' | 'permanent-delete'): Promise<void> {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Must be logged in to delete account');

  const response = await fetch(getApiUrl(`/api/accounts/${encodeURIComponent(accountId)}/${action}`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
}

const isTrialUser = (userData: Record<string, unknown> | undefined) => {
  return userData?.isTrial === true || userData?.isTrial === 'true';
};

async function resolveAccountLockTier(userId: string): Promise<boolean> {
  const hint = getAuthBasicTierLimitedHint();
  if (typeof hint === 'boolean') {
    return hint;
  }
  return resolveIsBasicLimitedTier(userId);
}

export const getAccounts = async (): Promise<TradingAccount[]> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Must be logged in to fetch accounts');

    const accountsRef = collection(db, ACCOUNTS_COLLECTION);
    const q = query(accountsRef, where('userId', '==', user.uid));
    const querySnapshot = await getDocs(q);

    const accounts = querySnapshot.docs
      .map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
      } as TradingAccount))
      .filter(account => !isPendingPermanentDeletion(account));
    const hint = getAuthBasicTierLimitedHint();
    const isLimitedTier = typeof hint === 'boolean' ? hint : await resolveAccountLockTier(user.uid);

    return syncBasicTierAccountLock(accounts, isLimitedTier);
  } catch (error) {
    // console.error supprimé
    throw error;
  }
};

export const accountService = {
  async createAccount(account: Omit<TradingAccount, 'id'>): Promise<TradingAccount> {
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('Must be logged in to create account');
    }

    // 🚀 CORRECTION: Validation des données d'entrée
    if (!account.name || typeof account.name !== 'string' || account.name.trim().length === 0) {
      throw new Error('Le nom du compte est requis');
    }

    // Pour les comptes MetaTrader, initialBalance et currency sont optionnels (récupérés automatiquement)
    const hasMetaTrader = account.metaTrader && account.metaTrader.server && account.metaTrader.login;
    
    if (!hasMetaTrader) {
      if (!account.initialBalance || typeof account.initialBalance !== 'number' || account.initialBalance <= 0) {
        throw new Error('Le solde initial doit être un nombre positif');
      }

      if (!account.currency || typeof account.currency !== 'string') {
        throw new Error('La devise est requise');
      }
    }

    try {
      const userDoc = await getDoc(doc(db, USERS_COLLECTION, user.uid));
      const userData = userDoc.data() as Record<string, unknown> | undefined;
      const isTrial = isTrialUser(userData);
      const trialLockRef = doc(db, TRIAL_ACCOUNT_LOCKS_COLLECTION, user.uid);

      if (isTrial) {
        const trialLockDoc = await getDoc(trialLockRef);
        if (trialLockDoc.exists()) {
          throw new Error('La limite trial d\'un compte maximum à vie a déjà été atteinte');
        }
      }

      // 🚀 CORRECTION: Nettoyer l'objet account en supprimant les champs problématiques
      const cleanedAccount: any = {};
      
      Object.keys(account).forEach(key => {
        const value = (account as any)[key];
        
        // Exclure les valeurs undefined car Firestore ne les accepte pas
        if (value === undefined) {
          // console.warn supprimé
          return;
        }
        
        // Vérifier si la valeur est un deleteField() 
        if (value && typeof value === 'object' && value.constructor && value.constructor.name === 'FieldValue') {
          // console.warn supprimé
          return;
        }
        
        // Inclure la valeur valide
        cleanedAccount[key] = value;
      });

      const accountData = {
        ...cleanedAccount,
        userId: user.uid,
        currentBalance: account.initialBalance || 0,
        createdAt: new Date().toISOString(),
        // 🚀 CORRECTION: Ajout de champs par défaut manquants
        updatedAt: new Date().toISOString(),
        // Ne pas inclure accountVerified pour les comptes manuels, il sera ajouté automatiquement pour les comptes MetaTrader
      };

      console.log('🚀 Tentative d\'ajout du document Firestore avec les données:', {
        ...accountData,
        metaTrader: accountData.metaTrader ? {
          ...accountData.metaTrader,
          password: '[MASQUÉ]'
        } : undefined
      });

      let accountId: string;

      if (isTrial) {
        const accountRef = doc(collection(db, ACCOUNTS_COLLECTION));
        const batch = writeBatch(db);

        batch.set(accountRef, accountData);
        batch.set(trialLockRef, {
          userId: user.uid,
          locked: true,
          createdAt: serverTimestamp()
        });

        await batch.commit();
        accountId = accountRef.id;
      } else {
        const docRef = await addDoc(collection(db, ACCOUNTS_COLLECTION), accountData);
        accountId = docRef.id;
      }
      
      // console.log supprimé
      // La création est captée automatiquement par le listener temps réel des comptes.

      return {
        ...accountData,
        id: accountId
      } as TradingAccount;
      
    } catch (error) {
      // console.error supprimé
      
      // 🚀 CORRECTION: Messages d'erreur plus spécifiques basés sur le type d'erreur
      if (error instanceof Error) {
        if (error.message.includes('permission-denied')) {
          throw new Error('Permissions insuffisantes pour créer un compte. Vérifiez votre connexion.');
        } else if (error.message.includes('network')) {
          throw new Error('Problème de connexion réseau. Vérifiez votre connexion internet.');
        } else if (error.message.includes('quota')) {
          throw new Error('Quota de base de données dépassé. Veuillez réessayer plus tard.');
        } else if (error.message.includes('invalid-argument')) {
          throw new Error('Données invalides fournies pour la création du compte.');
        } else {
          throw new Error(`Erreur lors de la création du compte: ${error.message}`);
        }
      } else {
        throw new Error('Erreur inconnue lors de la création du compte');
      }
    }
  },

  getAccounts,

  async updateMetaTraderInfo(accountId: string, metaTraderInfo: { server: string; login: string; password: string }): Promise<TradingAccount> {
    const user = auth.currentUser;
    
    if (!user) throw new Error('Must be logged in to update MetaTrader info');
    
    try {
      const docRef = doc(db, ACCOUNTS_COLLECTION, accountId);
      
      // Vérifier que le compte appartient à l'utilisateur
      const accountSnap = await getDoc(docRef);
      if (!accountSnap.exists()) throw new Error('Account not found');
      if (accountSnap.data().userId !== user.uid) throw new Error('Unauthorized');
      
      // Mettre à jour les informations MetaTrader
      await updateDoc(docRef, {
        metaTrader: {
          platform: 'MT4',
          server: metaTraderInfo.server,
          login: metaTraderInfo.login,
          password: metaTraderInfo.password,
          updatedAt: new Date().toISOString()
        }
      });

      // Récupérer et retourner le compte mis à jour
      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data()
      } as TradingAccount;
    } catch (error) {
      // console.error supprimé
      throw new Error('Failed to update MetaTrader info');
    }
  },

  async getAccount(accountId: string): Promise<TradingAccount | null> {
    const user = auth.currentUser;
    
    if (!user) throw new Error('Must be logged in to fetch account');
    
    try {
      const docRef = doc(db, ACCOUNTS_COLLECTION, accountId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Vérifier que le compte appartient à l'utilisateur
        if (data.userId !== user.uid) throw new Error('Unauthorized');
        return {
          id: docSnap.id,
          ...data
        } as TradingAccount;
      }
      
      return null;
    } catch (error) {
      // console.error supprimé
      throw new Error('Failed to fetch account');
    }
  },

  async updateAccount(accountId: string, updates: Partial<TradingAccount>): Promise<TradingAccount> {
    const user = auth.currentUser;
    
    if (!user) throw new Error('Must be logged in to update account');
    
    try {
      const docRef = doc(db, ACCOUNTS_COLLECTION, accountId);
      
      // Vérifier que le compte appartient à l'utilisateur
      const accountSnap = await getDoc(docRef);
      if (!accountSnap.exists()) throw new Error('Account not found');
      if (accountSnap.data().userId !== user.uid) throw new Error('Unauthorized');
      
      await updateDoc(docRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      });

      // Récupérer et retourner le compte mis à jour
      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data()
      } as TradingAccount;
    } catch (error) {
      // console.error supprimé
      throw new Error('Failed to update account');
    }
  },

  /**
   * Désactive un compte (le masque partout dans l'app, réactivable à tout moment)
   */
  async disableAccount(accountId: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('Must be logged in to disable account');
    const accountDoc = await getDoc(doc(db, ACCOUNTS_COLLECTION, accountId));
    if (!accountDoc.exists()) throw new Error('Account not found');
    if (accountDoc.data().userId !== user.uid) throw new Error('Unauthorized');
    await updateDoc(doc(db, ACCOUNTS_COLLECTION, accountId), {
      isDisabled: true,
      disabledByBasicTier: false,
      disabledAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
  },

  /**
   * Réactive un compte désactivé
   */
  async reactivateAccount(accountId: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('Must be logged in to reactivate account');
    const accountDoc = await getDoc(doc(db, ACCOUNTS_COLLECTION, accountId));
    if (!accountDoc.exists()) throw new Error('Account not found');
    if (accountDoc.data().userId !== user.uid) throw new Error('Unauthorized');
    await updateDoc(doc(db, ACCOUNTS_COLLECTION, accountId), {
      isDisabled: false,
      disabledByBasicTier: false,
      disabledAt: null,
      updatedAt: new Date().toISOString()
    });
  },

  /**
   * Met un compte en corbeille (soft delete) via le proxy : restaurable 30 jours, puis purgé
   * par le cron serveur. Le proxy pose aussi le verrou trial si besoin.
   */
  async deleteAccount(accountId: string): Promise<void> {
    try {
      await callAccountDeletionEndpoint(accountId, 'delete');
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to delete account: ${error.message}`);
      } else {
        throw new Error('Failed to delete account: An unknown error occurred');
      }
    }
  },

  /**
   * Restaure un compte supprimé (annule le soft delete)
   */
  async restoreAccount(accountId: string): Promise<void> {
    const user = auth.currentUser;
    
    if (!user) throw new Error('Must be logged in to restore account');
    
    try {
      // Vérifier que le compte existe et appartient à l'utilisateur
      const accountDoc = await getDoc(doc(db, ACCOUNTS_COLLECTION, accountId));
      if (!accountDoc.exists()) {
        throw new Error('Account not found');
      }
      if (accountDoc.data().userId !== user.uid) {
        throw new Error('Unauthorized to restore this account');
      }

      const accountRef = doc(db, ACCOUNTS_COLLECTION, accountId);
      await updateDoc(accountRef, {
        deleted: false,
        deletedAt: null,
        restoredAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error restoring account:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to restore account: ${error.message}`);
      } else {
        throw new Error('Failed to restore account: An unknown error occurred');
      }
    }
  },

  /**
   * Suppression définitive d'un compte déjà en corbeille, via le proxy (refusée sinon).
   *
   * Le navigateur ne supprime aucun trade : télécharger puis effacer des milliers de trades
   * côté client (cache IndexedDB persistant + une mutation locale par trade) gelait la page et
   * laissait un compte à moitié purgé si l'onglet se fermait. Le proxy pose
   * `permanentDeletionRequestedAt` (le compte disparaît aussitôt de l'UI), répond, puis purge
   * trades et compte via l'Admin SDK ; le cron horaire reprend toute purge interrompue.
   */
  async permanentlyDeleteAccount(accountId: string): Promise<void> {
    try {
      await callAccountDeletionEndpoint(accountId, 'permanent-delete');
    } catch (error) {
      console.error('Error permanently deleting account:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to permanently delete account: ${error.message}`);
      } else {
        throw new Error('Failed to permanently delete account: An unknown error occurred');
      }
    }
  },

  async updateExistingAccountsPlatform(): Promise<void> {
    const user = auth.currentUser;
    
    if (!user) throw new Error('Must be logged in to update accounts');
    
    try {
      const accounts = await this.getAccounts();
      const batch = writeBatch(db);
      
      accounts.forEach(account => {
        if (account.metaTrader && !account.metaTrader.platform) {
          const docRef = doc(db, ACCOUNTS_COLLECTION, account.id);
          batch.update(docRef, {
            'metaTrader.platform': 'MT4'
          });
        }
      });
      
      await batch.commit();
    } catch (error) {
      // console.error supprimé
      throw new Error('Failed to update existing accounts platform');
    }
  },

  /**
   * S'abonne aux changements des comptes de l'utilisateur en temps réel
   * @param callback Fonction appelée à chaque mise à jour des comptes
   * @returns Fonction pour se désabonner
   */
  subscribeToAccounts(callback: (accounts: TradingAccount[]) => void) {
    const user = auth.currentUser;

    if (!user) {
      throw new Error('Must be logged in to subscribe to accounts');
    }

    ensureSharedAccountsSubscription(user.uid);
    sharedSubscribers.add(callback);

    // Si un autre composant écoutait déjà, on renvoie immédiatement l'état en cache
    // pour ce nouvel abonné, sans lecture Firestore supplémentaire.
    if (sharedAccountsFingerprint !== null) {
      void emitSharedAccounts(sharedLastAccounts, user.uid);
    }

    return () => {
      sharedSubscribers.delete(callback);
      if (sharedSubscribers.size === 0) {
        teardownSharedAccountsSubscription();
      }
    };
  },

  /**
   * S'abonne aux changements d'un compte spécifique en temps réel
   * @param accountId ID du compte à suivre
   * @param callback Fonction appelée à chaque mise à jour du compte
   * @returns Fonction pour se désabonner
   */
  subscribeToAccount(accountId: string, callback: (account: TradingAccount | null) => void) {
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('Must be logged in to subscribe to account');
    }

    const accountRef = doc(db, ACCOUNTS_COLLECTION, accountId);
    
    // 🚀 CORRECTION: Utiliser onSnapshot pour recevoir les mises à jour
    return onSnapshot(accountRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const account = {
          id: snapshot.id,
          ...data,
        } as TradingAccount;
        
        callback(account);
      } else {
        // Le compte a été supprimé
        callback(null);
      }
    }, (error) => {
      console.error('[accountService.subscribeToAccount] Firestore listener error:', error);
      callback(null);
    });
  },
};

export default accountService;
